import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// KOA-5857 — Tiny right-click menu for grid rows.
//
// Built locally instead of pulling in antd's Dropdown so we control the
// portal, the outside-click handling, and the keyboard semantics — and so
// it composes cleanly with the existing Kuika tokens (--dark, --blue-5,
// etc.) used by the rest of the editor.
//
// ARIA + keyboard contract (WAI-ARIA APG "Menu" pattern):
//   - Root has role="menu" with aria-orientation="vertical".
//   - Items have role="menuitem", aria-disabled, tabIndex=-1.
//   - Submenu triggers add aria-haspopup="menu" + aria-expanded.
//   - Separators have role="separator".
//   - Initial focus on open: first non-disabled non-separator item.
//   - Arrow Up/Down rove focus, wrapping at the ends.
//   - Home / End jump to first / last focusable item.
//   - Enter / Space activate the focused item (same as click).
//   - ArrowRight on a focused submenu trigger opens it and moves focus
//     to the submenu's first item. ArrowLeft inside a submenu closes
//     it and returns focus to the trigger.
//   - Escape closes the open submenu first (if any), then closes the
//     root menu on a second Escape.
//   - Focus on unmount restores to whatever owned it before open.

export interface IRowContextMenuItem {
  type: "item";
  label: string;
  onClick: () => void;
  disabled?: boolean;
  // Native-title tooltip for the item — surfaces on hover via the
  // host's tooltip layer. Mainly used to EXPLAIN why a disabled item
  // is disabled (e.g. "Column is not nullable") so the affordance
  // stays discoverable but the user knows the reason without
  // hunting through schema.
  title?: string;
  // True for checkbox-style menu items: clicking runs onClick but
  // leaves the menu open so the user can toggle multiple entries
  // without re-opening (e.g. the Table Editor's "Columns" picker).
  // Default false matches classic right-click menu semantics.
  keepOpenOnClick?: boolean;
  // Opt-in `data-testid` for the item's button. Menu items are built from
  // labels, so an item that a test has to click needs a stable name that does
  // not move with its copy — only the items under test carry one.
  dataTestId?: string;
}
export interface IRowContextMenuSeparator {
  type: "separator";
}
export interface IRowContextMenuSubmenu {
  type: "submenu";
  label: string;
  options: IRowContextMenuOption[];
}
export type IRowContextMenuOption = IRowContextMenuItem | IRowContextMenuSeparator | IRowContextMenuSubmenu;

export interface IRowContextMenuProps {
  x: number;
  y: number;
  options: IRowContextMenuOption[];
  onClose: () => void;
}

const MENU_WIDTH: number = 320;
const MENU_VIEWPORT_MARGIN: number = 8;
// Submenu top anchor — flush with the parent item's top so the chevron
// and the first submenu entry line up visually. Slight negative nudge
// compensates for the parent's 4px border-radius / item padding.
const SUB_MENU_TOP_NUDGE: number = -4;

interface ICoords {
  x: number;
  y: number;
}

// Resolved submenu placement after measurement. Top / max-height are
// computed against the parent item rect AND the submenu's own height
// so the menu always sits inside the viewport — flips up when there's
// no room below, clamps to whichever side has more space when there's
// no room either way, and uses its own scroll only as a last resort.
interface ISubMenuPlacement {
  top: number;
  left: number | "auto";
  right: number | "auto";
  maxHeight: number;
  ready: boolean;
}

// Clamp menu position so it never spills off the viewport.
const clampToViewport = (anchor: ICoords, est: ICoords): ICoords => {
  const vw: number = window.innerWidth;
  const vh: number = window.innerHeight;
  const menuWidth: number = Math.min(MENU_WIDTH, Math.max(220, vw - MENU_VIEWPORT_MARGIN * 2));
  const x: number =
    anchor.x + est.x + menuWidth > vw
      ? Math.max(MENU_VIEWPORT_MARGIN, vw - menuWidth - MENU_VIEWPORT_MARGIN)
      : anchor.x + est.x;
  // Bottom clamp is approximate — measured after mount in the layout effect.
  const y: number = anchor.y + est.y;
  return { x, y };
};

// True for options the user can land on with roving focus. Separators
// are visual only; disabled items skip focus per APG. Submenu triggers
// are focusable even when their child set is empty — the user might
// arrow into and back out of an empty submenu (degenerate but valid).
const isFocusableOption = (option: IRowContextMenuOption): boolean => {
  if (option.type === "separator") {
    return false;
  }
  if (option.type === "item" && option.disabled === true) {
    return false;
  }
  return true;
};

// Walk options[] in the given direction, returning the next focusable
// index after `from`. Wraps at both ends. Returns -1 only when no
// option is focusable (degenerate: all separators / all disabled).
const nextFocusable = (options: IRowContextMenuOption[], from: number, dir: 1 | -1): number => {
  if (options.length === 0) {
    return -1;
  }
  for (let step: number = 1; step <= options.length; step += 1) {
    const candidate: number = (from + dir * step + options.length * step) % options.length;
    if (isFocusableOption(options[candidate]!)) {
      return candidate;
    }
  }
  return -1;
};

const firstFocusableIndex = (options: IRowContextMenuOption[]): number => {
  for (let i: number = 0; i < options.length; i += 1) {
    if (isFocusableOption(options[i]!)) {
      return i;
    }
  }
  return -1;
};

const lastFocusableIndex = (options: IRowContextMenuOption[]): number => {
  for (let i: number = options.length - 1; i >= 0; i -= 1) {
    if (isFocusableOption(options[i]!)) {
      return i;
    }
  }
  return -1;
};

interface ISubMenuProps {
  parentRef: React.RefObject<HTMLElement>;
  options: IRowContextMenuOption[];
  renderOption: (option: IRowContextMenuOption, idx: number, container: "root" | "submenu") => JSX.Element;
  onCloseSubmenu: () => void;
  // Roving focus inside the submenu. Capped to options.length; -1 means
  // none focused yet (initial state before keyboard nav).
  focusedIndex: number;
  onFocusedIndexChange: (idx: number) => void;
}

// Submenu — owns its own measurement + placement. Rendered inline as a
// descendant of the parent item, but uses `position: fixed` so it
// escapes any ancestor layout / overflow context. After mount, measures
// the parent item rect and its own intrinsic height, then picks a side
// (right of parent if it fits, otherwise left) and a vertical anchor
// (down from parent top, up from parent bottom, or whichever has more
// room) with a max-height capped at the actual available space.
const SubMenu: React.FC<ISubMenuProps> = (props: ISubMenuProps): JSX.Element => {
  const { parentRef, options, renderOption, onCloseSubmenu, focusedIndex, onFocusedIndexChange } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<ISubMenuPlacement>({
    top: -9999,
    left: -9999,
    right: "auto",
    maxHeight: 0,
    ready: false
  });

  // Measure once on mount. Re-running on options changes would loop —
  // the parent re-creates the options array each render, the effect
  // fires, setPlacement bumps a fresh object, render again, effect
  // again. The submenu's content doesn't grow during the user's hover
  // session, so a one-shot measurement is correct.
  useLayoutEffect((): void => {
    if (!parentRef.current || !ref.current) {
      return;
    }
    const parentRect: DOMRect = parentRef.current.getBoundingClientRect();
    const ownRect: DOMRect = ref.current.getBoundingClientRect();
    const vw: number = window.innerWidth;
    const vh: number = window.innerHeight;
    const margin: number = MENU_VIEWPORT_MARGIN;
    const subWidth: number = Math.max(ownRect.width, 220);
    const subHeight: number = ownRect.height;

    // Horizontal placement — prefer opening to the right of the parent
    // item. Flip left only when right would overflow AND left has room.
    const spaceRight: number = vw - parentRect.right - margin;
    const spaceLeft: number = parentRect.left - margin;
    const placeLeft: boolean = spaceRight < subWidth && spaceLeft >= subWidth;
    const left: number | "auto" = placeLeft ? "auto" : parentRect.right;
    const right: number | "auto" = placeLeft ? vw - parentRect.left : "auto";

    // Vertical placement — align top with parent.top by default, plus a
    // small nudge to line the first item up with the parent item label.
    // Flip up (anchor bottom to parent.bottom) when down would overflow
    // and up has more room. Falls back to the side with the most room
    // (scroll inside the submenu) when neither side fits cleanly.
    const desiredTop: number = parentRect.top + SUB_MENU_TOP_NUDGE;
    const spaceBelow: number = vh - desiredTop - margin;
    const spaceAbove: number = parentRect.bottom - margin;

    let top: number;
    let maxHeight: number;
    if (subHeight <= spaceBelow) {
      top = desiredTop;
      maxHeight = spaceBelow;
    } else if (subHeight <= spaceAbove) {
      top = parentRect.bottom - subHeight;
      maxHeight = spaceAbove;
    } else if (spaceBelow >= spaceAbove) {
      top = desiredTop;
      maxHeight = spaceBelow;
    } else {
      top = margin;
      maxHeight = parentRect.bottom - margin;
    }
    setPlacement({ top, left, right, maxHeight: Math.max(60, maxHeight), ready: true });
    // Intentionally empty deps — measure on mount only. parentRef is a
    // ref (stable), and re-measuring on the options array would loop
    // because the parent re-creates that array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First render before measurement: hide off-screen instead of flashing
  // at (0, 0). Once useLayoutEffect runs (synchronously before paint)
  // the placement becomes ready and the submenu pops into the right
  // spot without a visible jump.
  const inlineStyle: React.CSSProperties = {
    position: "fixed",
    top: placement.top,
    left: placement.left,
    right: placement.right,
    maxHeight: placement.maxHeight,
    overflowY: "auto",
    visibility: placement.ready ? "visible" : "hidden"
  };

  // Submenu owns its own keyboard navigation. ArrowLeft / Escape close
  // the submenu and return focus to the trigger (handled in the parent
  // via the onCloseSubmenu callback). ArrowUp / ArrowDown / Home / End
  // rove focus within the submenu. Activation goes through the
  // individual item buttons' onClick.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      onFocusedIndexChange(nextFocusable(options, focusedIndex, 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      onFocusedIndexChange(nextFocusable(options, focusedIndex, -1));
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      e.stopPropagation();
      onFocusedIndexChange(firstFocusableIndex(options));
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      e.stopPropagation();
      onFocusedIndexChange(lastFocusableIndex(options));
      return;
    }
    if (e.key === "ArrowLeft" || e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCloseSubmenu();
      return;
    }
  };

  return (
    <div
      ref={ref}
      className="kuika-table-editor__ctxmenu kuika-table-editor__ctxmenu--sub"
      style={inlineStyle}
      role="menu"
      aria-orientation="vertical"
      onKeyDown={onKeyDown}
    >
      {options.map((opt: IRowContextMenuOption, idx: number): JSX.Element => renderOption(opt, idx, "submenu"))}
    </div>
  );
};

interface ISubmenuItemProps {
  option: IRowContextMenuSubmenu;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  renderOption: (option: IRowContextMenuOption, idx: number, container: "root" | "submenu") => JSX.Element;
  itemRef: React.RefObject<HTMLDivElement>;
  // True when the trigger itself owns roving focus in the root menu.
  isRootFocused: boolean;
  // Independent roving inside the open submenu.
  submenuFocusedIndex: number;
  onSubmenuFocusedIndexChange: (idx: number) => void;
}

// Submenu trigger row + the SubMenu it opens. The trigger's own DOM
// node carries roving focus; opening / closing the submenu is handled
// by the parent (mouse: hover; keyboard: ArrowRight / ArrowLeft).
const SubmenuItem: React.FC<ISubmenuItemProps> = (props: ISubmenuItemProps): JSX.Element => {
  const {
    option,
    isOpen,
    onOpen,
    onClose,
    renderOption,
    itemRef,
    isRootFocused,
    submenuFocusedIndex,
    onSubmenuFocusedIndexChange
  } = props;
  return (
    <div
      ref={itemRef}
      className={
        "kuika-table-editor__ctxmenu-item kuika-table-editor__ctxmenu-item--submenu" + (isOpen ? " is-open" : "")
      }
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      tabIndex={isRootFocused ? 0 : -1}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
    >
      <span className="kuika-table-editor__ctxmenu-item-label">{option.label}</span>
      <span className="kuika-table-editor__ctxmenu-item-chev">›</span>
      {isOpen && (
        <SubMenu
          parentRef={itemRef}
          options={option.options}
          renderOption={renderOption}
          onCloseSubmenu={onClose}
          focusedIndex={submenuFocusedIndex}
          onFocusedIndexChange={onSubmenuFocusedIndexChange}
        />
      )}
    </div>
  );
};

const RowContextMenu: React.FC<IRowContextMenuProps> = (props: IRowContextMenuProps): JSX.Element => {
  const { x, y, options, onClose } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  const [pos, setPos] = useState<ICoords>(clampToViewport({ x, y }, { x: 0, y: 0 }));
  const menuWidth: number = Math.min(MENU_WIDTH, Math.max(220, window.innerWidth - MENU_VIEWPORT_MARGIN * 2));

  // Roving focus. `focusedIndex` tracks which root-menu option owns the
  // tab stop; `submenuFocusedIndex` does the same inside the open
  // submenu. Both start at the first focusable item once the menu has
  // mounted so the user can immediately use arrow keys.
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [submenuFocusedIndex, setSubmenuFocusedIndex] = useState<number>(-1);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const submenuItemRefs = useRef<(HTMLElement | null)[]>([]);
  // Captured at mount time; restored on unmount so the focus ring lands
  // back where the user opened the menu from — without this, closing
  // the menu drops focus to `<body>` and a keyboard user loses their
  // place in the grid / toolbar.
  const previousFocusRef = useRef<HTMLElement | null>(null);
  // Set to true when the menu closed because the user pressed Tab.
  // The unmount cleanup reads this and SKIPS the focus restore — Tab
  // is the user explicitly saying "I want focus on the next page
  // element", so pulling focus back to the opener would be wrong.
  const closedViaTabRef = useRef<boolean>(false);

  // Re-measure after mount so we know the real height and can clamp the y
  // axis if the menu would extend past the viewport bottom.
  useLayoutEffect((): void => {
    if (!rootRef.current) {
      return;
    }
    const rect = rootRef.current.getBoundingClientRect();
    const vh: number = window.innerHeight;
    const overflowY: number = Math.max(0, y + rect.height + 8 - vh);
    if (overflowY > 0) {
      setPos((prev: ICoords): ICoords => ({ x: prev.x, y: Math.max(8, y - overflowY) }));
    }
  }, [y]);

  // Capture pre-open focus + initial roving focus, restore on unmount.
  useEffect((): (() => void) => {
    const active: Element | null = document.activeElement;
    previousFocusRef.current = active instanceof HTMLElement ? active : null;
    setFocusedIndex(firstFocusableIndex(options));
    return (): void => {
      const target: HTMLElement | null = previousFocusRef.current;
      previousFocusRef.current = null;
      // Skip the restore when the menu closed via Tab — the browser
      // moved focus to the next page element, that's where the user
      // wants it. Without this guard, the unmount would yank focus
      // back to the opener and the Tab key would feel broken.
      if (closedViaTabRef.current) {
        closedViaTabRef.current = false;
        return;
      }
      if (target !== null && document.body.contains(target)) {
        target.focus();
      }
    };
    // Intentionally one-shot — `options` may re-render with a fresh
    // array identity each parent render, but the menu's set of options
    // for THIS open session is stable. Bumping focus on every render
    // would fight the user's roving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drive DOM focus from `focusedIndex`. Skipped while a submenu is
  // open — focus belongs to the submenu in that case.
  useEffect((): void => {
    if (openSubmenu !== null) {
      return;
    }
    if (focusedIndex < 0) {
      return;
    }
    const node: HTMLElement | null = itemRefs.current[focusedIndex] ?? null;
    if (node !== null) {
      node.focus();
    }
  }, [focusedIndex, openSubmenu]);

  // Same idea for the submenu — drive DOM focus from
  // `submenuFocusedIndex` whenever a submenu is open.
  useEffect((): void => {
    if (openSubmenu === null) {
      return;
    }
    if (submenuFocusedIndex < 0) {
      return;
    }
    const node: HTMLElement | null = submenuItemRefs.current[submenuFocusedIndex] ?? null;
    if (node !== null) {
      node.focus();
    }
  }, [openSubmenu, submenuFocusedIndex]);

  // Outside click close. The opening contextmenu event is still
  // mid-dispatch when this effect runs (capture phase elsewhere fired before
  // bubble), so we defer attaching the listener by a frame to avoid closing
  // the menu on the very event that just opened it. Keyboard (Escape) is
  // handled by the root onKeyDown below so we don't fight focus.
  useEffect((): (() => void) => {
    const onPointerDown = (e: MouseEvent): void => {
      if (rootRef.current && rootRef.current.contains(e.target as Node)) {
        return;
      }
      onClose();
    };
    const raf: number = window.requestAnimationFrame((): void => {
      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("contextmenu", onPointerDown);
    });
    return (): void => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("contextmenu", onPointerDown);
    };
  }, [onClose]);

  // Submenu-of-the-focused-item — the user can ArrowRight to open a
  // submenu only if the currently focused item IS a submenu. Memoised
  // because options can be a fresh array every render.
  const focusedOption: IRowContextMenuOption | undefined = useMemo((): IRowContextMenuOption | undefined => {
    if (focusedIndex < 0 || focusedIndex >= options.length) {
      return undefined;
    }
    return options[focusedIndex];
  }, [focusedIndex, options]);

  // Open a submenu and immediately move keyboard focus to its first
  // item. The submenu component takes over focus management until the
  // user closes it.
  const openSubmenuAt = useCallback(
    (idx: number): void => {
      const opt: IRowContextMenuOption | undefined = options[idx];
      if (opt === undefined || opt.type !== "submenu") {
        return;
      }
      setOpenSubmenu(idx);
      setSubmenuFocusedIndex(firstFocusableIndex(opt.options));
      submenuItemRefs.current = [];
    },
    [options]
  );

  const closeOpenSubmenu = useCallback((): void => {
    setOpenSubmenu(null);
    setSubmenuFocusedIndex(-1);
  }, []);

  // Root menu keyboard handler — runs on the portal root. Submenu-local
  // keys (Up/Down/Home/End/Left/Esc inside a submenu) are handled
  // inside the SubMenu component and stop propagation, so they don't
  // double-fire here.
  const onRootKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (openSubmenu !== null) {
        closeOpenSubmenu();
        return;
      }
      onClose();
      return;
    }
    if (e.key === "Tab") {
      // Per APG menu pattern: Tab moves focus to the next focusable
      // page element AND closes the menu. We do NOT preventDefault —
      // the browser's native Tab handling does the focus move; we
      // just close the portal. The closedViaTabRef flag tells the
      // unmount cleanup to skip the focus restore so we don't yank
      // focus back to the opener.
      closedViaTabRef.current = true;
      onClose();
      return;
    }
    if (openSubmenu !== null) {
      // While a submenu is open, root navigation is suppressed — the
      // submenu owns focus.
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(nextFocusable(options, focusedIndex, 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(nextFocusable(options, focusedIndex, -1));
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setFocusedIndex(firstFocusableIndex(options));
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      setFocusedIndex(lastFocusableIndex(options));
      return;
    }
    if (e.key === "ArrowRight") {
      if (focusedOption?.type === "submenu") {
        e.preventDefault();
        openSubmenuAt(focusedIndex);
      }
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      // Don't intercept Space inside a focused button — the browser's
      // own activation is correct (calls onClick) and double-firing
      // would invoke the action twice.
      if (focusedOption?.type === "item") {
        // Let the native button handle it.
        return;
      }
      if (focusedOption?.type === "submenu") {
        e.preventDefault();
        openSubmenuAt(focusedIndex);
      }
    }
  };

  const renderOption = (option: IRowContextMenuOption, idx: number, container: "root" | "submenu"): JSX.Element => {
    if (option.type === "separator") {
      return <div key={`sep-${idx}`} className="kuika-table-editor__ctxmenu-sep" role="separator" />;
    }
    if (option.type === "submenu") {
      const isOpen: boolean = container === "root" && openSubmenu === idx;
      const itemRef = React.createRef<HTMLDivElement>();
      // Submenu triggers register into the root ref array — they can
      // own roving focus the same way items do.
      const isRootFocused: boolean = container === "root" && focusedIndex === idx;
      return (
        <SubmenuItemRefRegister
          key={`sub-${idx}`}
          itemRef={itemRef}
          container={container}
          idx={idx}
          itemRefs={itemRefs}
          submenuItemRefs={submenuItemRefs}
        >
          <SubmenuItem
            option={option}
            isOpen={isOpen}
            onOpen={(): void => {
              if (container === "root") {
                setOpenSubmenu(idx);
                setSubmenuFocusedIndex(firstFocusableIndex(option.options));
                submenuItemRefs.current = [];
              }
            }}
            onClose={(): void => {
              if (container === "root") {
                setOpenSubmenu(null);
                setSubmenuFocusedIndex(-1);
                // Return focus to the trigger so the user resumes
                // arrow-nav at the right spot.
                setFocusedIndex(idx);
              }
            }}
            renderOption={renderOption}
            itemRef={itemRef}
            isRootFocused={isRootFocused}
            submenuFocusedIndex={submenuFocusedIndex}
            onSubmenuFocusedIndexChange={setSubmenuFocusedIndex}
          />
        </SubmenuItemRefRegister>
      );
    }
    const isCurrentlyFocused: boolean = container === "root" ? focusedIndex === idx : submenuFocusedIndex === idx;
    return (
      <button
        data-testid={option.dataTestId}
        key={`item-${idx}`}
        type="button"
        ref={(el: HTMLButtonElement | null): void => {
          if (container === "root") {
            itemRefs.current[idx] = el;
          } else {
            submenuItemRefs.current[idx] = el;
          }
        }}
        className="kuika-table-editor__ctxmenu-item"
        disabled={option.disabled}
        role="menuitem"
        aria-disabled={option.disabled ? true : undefined}
        title={option.title}
        tabIndex={isCurrentlyFocused ? 0 : -1}
        onMouseEnter={(): void => {
          // Hover updates roving focus so keyboard nav resumes from the
          // mouse-pointed item, matching native menu behaviour.
          if (container === "root") {
            setFocusedIndex(idx);
            if (openSubmenu !== null) {
              setOpenSubmenu(null);
              setSubmenuFocusedIndex(-1);
            }
          } else {
            setSubmenuFocusedIndex(idx);
          }
        }}
        onClick={(e: React.MouseEvent): void => {
          e.stopPropagation();
          if (option.disabled) {
            return;
          }
          option.onClick();
          if (!option.keepOpenOnClick) {
            onClose();
          }
        }}
      >
        <span className="kuika-table-editor__ctxmenu-item-label">{option.label}</span>
      </button>
    );
  };

  return createPortal(
    <div
      ref={rootRef}
      className="kuika-table-editor__ctxmenu"
      style={{ left: pos.x, top: pos.y, width: menuWidth }}
      role="menu"
      aria-orientation="vertical"
      onContextMenu={(e: React.MouseEvent): void => e.preventDefault()}
      onKeyDown={onRootKeyDown}
    >
      {options.map((opt: IRowContextMenuOption, idx: number): JSX.Element => renderOption(opt, idx, "root"))}
    </div>,
    document.body
  );
};

// Tiny adapter that mirrors a submenu trigger's ref into the parent's
// `itemRefs` array so roving focus can target it. Wrapping the
// existing SubmenuItem keeps the ref registration logic out of that
// component's render path.
interface ISubmenuItemRefRegisterProps {
  itemRef: React.RefObject<HTMLDivElement>;
  container: "root" | "submenu";
  idx: number;
  itemRefs: React.MutableRefObject<(HTMLElement | null)[]>;
  submenuItemRefs: React.MutableRefObject<(HTMLElement | null)[]>;
  children: React.ReactNode;
}
const SubmenuItemRefRegister: React.FC<ISubmenuItemRefRegisterProps> = (
  props: ISubmenuItemRefRegisterProps
): JSX.Element => {
  const { itemRef, container, idx, itemRefs, submenuItemRefs, children } = props;
  useEffect((): void => {
    if (container === "root") {
      itemRefs.current[idx] = itemRef.current;
    } else {
      submenuItemRefs.current[idx] = itemRef.current;
    }
    // No cleanup — the array is rebuilt on each render of the parent
    // menu, and refs to unmounted nodes don't matter since the parent
    // re-creates the array fresh.
  });
  return <>{children}</>;
};

export default RowContextMenu;
